import * as React from 'react';
import {
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  Grid,
  Paper,
  Typography,
  useTheme,
} from '@mui/material';
import { AnnouncementOutlined, Check, Error, ExpandMore } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import { EnhancedAccordion, Loading, SubmitButton } from 'components';
import { EPaymentMethod } from 'enums';
import { BillingAddressForm, PaymentMethodForm, ShippingAddressForm } from 'forms';
import { ROUTES, ZONE_DECAL_PRICE } from 'consts';
import { getFullLocationString, numberWithCommas } from 'utils';
import { useAppDispatch, useAppSelector } from 'store/store.hook';
import { selectCurrentUserInfo } from 'store/slices/auth.slice';
import { PaymentCompleteDialog } from 'dialogs/payment';
import { updateLayout } from 'store/slices/layout.slice';
import { useGetItemsInCartQuery, usePlaceOrderMutation } from '../../store/apis/decal-order.api';
import { toast } from 'react-toastify';

interface CartPageV2Props {}

export const CartPageV2: React.FC<CartPageV2Props> = () => {
  const dispatch = useAppDispatch();

  const {
    orgSlug,
    billingAddress: cartBillingAddress,
    shippingAddress: cartShippingAddress,
    cartPaymentMethod,
  } = useAppSelector(selectCurrentUserInfo);

  const {
    data: order,
    isFetching: loadingItemsInCart,
    isSuccess: loadingItemsInCartSuccess,
    refetch,
  } = useGetItemsInCartQuery({ orgSlug });

  const [
    mutate,
    { isLoading: paymentProcessing, isSuccess: paymentProcessSuccess, isError: isPaymentError, error: paymentError },
  ] = usePlaceOrderMutation();

  const [quantity, setQuantity] = React.useState(0);

  const [visibleCheckoutResultDialog, setVisibleCheckoutResultDialog] = React.useState<boolean>(false);

  const [billingAddressUpdatingFlag, setBillingAddressUpdatingFlag] = React.useState<boolean>(false);
  const [shippingAddressUpdatingFlag, setShippingAddressUpdatingFlag] = React.useState<boolean>(false);
  const [paymentMethodUpdatingFlag, setPaymentMethodUpdatingFlag] = React.useState<boolean>(false);
  const cartGettingFlag = paymentProcessing;

  const billingAddressFormRef = React.useRef<any>();
  const shippingAddressFormRef = React.useRef<any>();
  const paymentMethodFormRef = React.useRef<any>();
  const theme = useTheme();
  const navigate = useNavigate();

  React.useEffect(() => {
    dispatch(updateLayout({ headerTitle: 'Cart' }));
    refetch();
  }, []);

  React.useEffect(() => {
    if (!loadingItemsInCart && loadingItemsInCartSuccess) {
      const totalQuantity = order.items.reduce((prev, curr) => prev + curr.quantity, 0);

      setQuantity(totalQuantity);
    }
  }, [loadingItemsInCart, loadingItemsInCartSuccess]);

  React.useEffect(() => {
    if (!paymentProcessing && paymentProcessSuccess) {
      // successfully processed the order, navigate to the active zones
      toast.success(<Msg msg={`Decal order successful.`} />, {
        autoClose: 5000,
      });

      navigate(`/${orgSlug}/${ROUTES.ZONES}/${ROUTES.ZONES_ACTIVE}`);
    }
  }, [paymentProcessing, paymentProcessSuccess]);

  React.useEffect(() => {
    if (isPaymentError) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      toast.error(<Msg msg={paymentError.data?.payload?.message} />);
    }
  }, [isPaymentError, paymentError]);

  const subtotal = (): number => {
    let total = 0;
    if (order) {
      total = quantity * ZONE_DECAL_PRICE;
    }

    return total;
  };

  const discount = 0;
  const shipping = 0;
  const tax = 0;
  const total = Math.round((subtotal() + discount + shipping + tax) * 100) / 100;

  const isPmType = !!cartPaymentMethod.pmType;
  let isPaymentMethod = false;

  if (isPmType && cartPaymentMethod.pmType === EPaymentMethod.INVOICE) {
    isPaymentMethod = !!cartPaymentMethod?.name && !!cartPaymentMethod?.email;
  } else {
    isPaymentMethod = !!cartPaymentMethod?.name && !!cartPaymentMethod?.pmLastFour;
  }

  const orderErrors = {
    billingAddress: !cartBillingAddress,
    shippingAddress: !cartShippingAddress,
    paymentMethod: !(isPmType && isPaymentMethod),
  };

  const placeOrder = () => {
    // place the order
    mutate({ orgSlug });
  };

  const Msg = ({ msg }: { msg: string }) => {
    return (
      <Box>
        <Typography variant="body2">{msg}</Typography>
      </Box>
    );
  };

  return (
    <>
      <Box sx={{ paddingX: 3, paddingY: 5 }}>
        {loadingItemsInCart && loadingItemsInCartSuccess ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: `calc(100vh - 300px)`,
              minHeight: 400,
            }}
          >
            <Loading text={'Loading order...'} />
          </Box>
        ) : order ? (
          <Grid container spacing={3}>
            <Grid item xs={12} lg={7}>
              <EnhancedAccordion gutter={24} dividerColor={'#efefef'}>
                <AccordionSummary
                  expandIcon={<ExpandMore color="inherit" />}
                  sx={{ paddingX: { xs: 3, sm: 5 }, paddingY: 2 }}
                >
                  BILLING ADDRESS
                  {orderErrors.billingAddress ? (
                    <Error sx={{ marginLeft: 2, fontSize: 22, color: theme.palette.error.main }} />
                  ) : (
                    <Check sx={{ marginLeft: 2, fontSize: 22, color: theme.palette.success.light }} />
                  )}
                </AccordionSummary>

                <AccordionDetails sx={{ paddingX: { xs: 3, sm: 5 }, paddingBottom: 5 }}>
                  <BillingAddressForm
                    ref={billingAddressFormRef}
                    onLoading={(loading) => setBillingAddressUpdatingFlag(loading)}
                  />

                  <SubmitButton
                    variant="text"
                    size="medium"
                    label="Update"
                    onClick={() => billingAddressFormRef.current.onSubmit()}
                    loading={billingAddressUpdatingFlag}
                    sx={{ marginTop: 4, paddingLeft: 2, paddingRight: 3 }}
                  />
                </AccordionDetails>
              </EnhancedAccordion>

              <EnhancedAccordion gutter={24} dividerColor={'#efefef'}>
                <AccordionSummary
                  expandIcon={<ExpandMore color="inherit" />}
                  sx={{ paddingX: { xs: 3, sm: 5 }, paddingY: 2 }}
                >
                  SHIPPING ADDRESS
                  {orderErrors.shippingAddress ? (
                    <Error sx={{ marginLeft: 2, fontSize: 22, color: theme.palette.error.main }} />
                  ) : (
                    <Check sx={{ marginLeft: 2, fontSize: 22, color: theme.palette.success.light }} />
                  )}
                </AccordionSummary>

                <AccordionDetails sx={{ paddingX: { xs: 3, sm: 5 }, paddingBottom: 4 }}>
                  <ShippingAddressForm
                    ref={shippingAddressFormRef}
                    onLoading={(loading) => setShippingAddressUpdatingFlag(loading)}
                  />

                  <SubmitButton
                    variant="text"
                    size="medium"
                    label="Update"
                    onClick={() => shippingAddressFormRef.current.onSubmit()}
                    loading={shippingAddressUpdatingFlag}
                    sx={{ marginTop: 4, paddingLeft: 2, paddingRight: 3 }}
                  />
                </AccordionDetails>
              </EnhancedAccordion>

              <EnhancedAccordion gutter={24} dividerColor={'#efefef'}>
                <AccordionSummary
                  expandIcon={<ExpandMore color="inherit" />}
                  sx={{ paddingX: { xs: 3, sm: 5 }, paddingY: 2 }}
                >
                  PAYMENT METHOD
                  {orderErrors.paymentMethod ? (
                    <Error sx={{ marginLeft: 2, fontSize: 22, color: theme.palette.error.main }} />
                  ) : (
                    <Check sx={{ marginLeft: 2, fontSize: 22, color: theme.palette.success.light }} />
                  )}
                </AccordionSummary>

                <AccordionDetails sx={{ paddingX: { xs: 3, sm: 5 }, paddingBottom: 5 }}>
                  <PaymentMethodForm
                    ref={paymentMethodFormRef}
                    onLoading={(loading) => setPaymentMethodUpdatingFlag(loading)}
                  />

                  <SubmitButton
                    variant="text"
                    size="medium"
                    label="Update"
                    onClick={() => paymentMethodFormRef.current.onSubmit()}
                    loading={paymentMethodUpdatingFlag}
                    sx={{ marginTop: 4, paddingLeft: 2, paddingRight: 3 }}
                  />
                </AccordionDetails>
              </EnhancedAccordion>
            </Grid>

            <Grid item xs={12} lg={5}>
              <Paper
                sx={{
                  borderTopWidth: 4,
                  borderTopColor: theme.palette.primary.main,
                  borderTopStyle: 'solid',
                  paddingX: 3,
                }}
              >
                <Box sx={{ paddingY: 3 }}>
                  <Typography>ORDER SUMMARY</Typography>
                </Box>

                <Grid container columnSpacing={4} rowSpacing={1}>
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        color: theme.palette.text.secondary,
                      }}
                    >
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          Subtotal
                        </Typography>
                        <Typography variant="caption">
                          {quantity} zones x ${ZONE_DECAL_PRICE}
                        </Typography>
                      </Box>

                      <Typography variant="subtitle2" sx={{ color: theme.palette.text.primary, lineHeight: 1 }}>
                        ${numberWithCommas(subtotal())}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="textSecondary">
                        Discount
                      </Typography>

                      <Typography variant="subtitle2" sx={{ color: theme.palette.text.primary }}>
                        ${numberWithCommas(discount)}
                      </Typography>
                    </Box>
                  </Grid> */}

                  <Grid item xs={12}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        color: theme.palette.text.secondary,
                      }}
                    >
                      <Typography variant="body2" color="textSecondary">
                        Shipping
                      </Typography>

                      <Typography variant="subtitle2" sx={{ color: theme.palette.text.primary, lineHeight: 1 }}>
                        ${numberWithCommas(shipping)}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        color: theme.palette.text.secondary,
                      }}
                    >
                      <Typography variant="body2" color="textSecondary">
                        Tax
                      </Typography>

                      <Typography variant="subtitle2" sx={{ color: theme.palette.text.primary, lineHeight: 1 }}>
                        ${numberWithCommas(tax)}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Box sx={{ paddingY: 2 }}>
                      <Divider />
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: '600' }}>
                        TOTAL
                      </Typography>

                      <Typography variant="subtitle1" sx={{ fontWeight: '600', color: theme.palette.primary.main }}>
                        ${numberWithCommas(total)}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Box sx={{ paddingY: 2 }}>
                      <Divider />
                    </Box>
                  </Grid>

                  {!orderErrors.billingAddress && (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography color="textSecondary" variant="body2">
                          Billing Address:
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.primary.main, marginLeft: 1 }}>
                          {getFullLocationString(cartBillingAddress)}
                        </Typography>
                      </Box>
                    </Grid>
                  )}

                  {!orderErrors.shippingAddress && (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography color="textSecondary" variant="body2">
                          Shipping Address:
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.primary.main, marginLeft: 1 }}>
                          {getFullLocationString(cartShippingAddress)}
                        </Typography>
                      </Box>
                    </Grid>
                  )}

                  {!orderErrors.paymentMethod && (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography color="textSecondary" variant="body2">
                          Name:
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.primary.main, marginLeft: 1 }}>
                          {cartPaymentMethod?.name || '-'}
                        </Typography>
                      </Box>
                    </Grid>
                  )}

                  {!orderErrors.paymentMethod && cartPaymentMethod?.pmType === EPaymentMethod.INVOICE && (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography color="textSecondary" variant="body2">
                          Email:
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.primary.main, marginLeft: 1 }}>
                          {cartPaymentMethod.email}
                        </Typography>
                      </Box>
                    </Grid>
                  )}

                  {!orderErrors.paymentMethod && cartPaymentMethod?.pmType === EPaymentMethod.CARD && (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography color="textSecondary" variant="body2">
                          Card:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: theme.palette.primary.main, marginLeft: 1, fontSize: 9 }}
                        >
                          xxxx xxxx xxxx xxxx
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.primary.main, marginLeft: 1 }}>
                          {cartPaymentMethod.pmLastFour}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', paddingY: 5 }}>
                    <Box>
                      <SubmitButton
                        label={'Purchase Decals'}
                        onClick={placeOrder}
                        disabled={
                          orderErrors.billingAddress ||
                          orderErrors.shippingAddress ||
                          orderErrors.paymentMethod ||
                          loadingItemsInCart
                        }
                        loading={cartGettingFlag}
                      />

                      <Box sx={{ textAlign: 'center', mt: 1 }}>
                        <Button onClick={() => navigate(`/${orgSlug}/cart/review`)}>Change Order</Button>
                      </Box>

                      <Box sx={{ marginLeft: 2, marginTop: 1 }}>
                        {orderErrors.billingAddress && (
                          <Typography variant="body2" sx={{ color: theme.palette.error.main }}>
                            * Invalid billing address.
                          </Typography>
                        )}

                        {orderErrors.shippingAddress && (
                          <Typography variant="body2" sx={{ color: theme.palette.error.main }}>
                            * Invalid shipping address.
                          </Typography>
                        )}

                        {orderErrors.paymentMethod && (
                          <Typography variant="body2" sx={{ color: theme.palette.error.main }}>
                            * Invalid payment method.
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: `calc(100vh - 300px)`,
              minHeight: 400,
            }}
          >
            <AnnouncementOutlined color="primary" sx={{ fontSize: 40 }} />
            <Typography color="textSecondary" sx={{ marginTop: 2 }}>
              No zones added to order
            </Typography>
            <Button onClick={() => navigate(`/${orgSlug}/${ROUTES.ORDER_DECALS}`)} sx={{ marginTop: 4, paddingX: 2 }}>
              Add zones to order
            </Button>
          </Box>
        )}
      </Box>

      <PaymentCompleteDialog visible={visibleCheckoutResultDialog} setVisible={setVisibleCheckoutResultDialog} />
    </>
  );
};
